/**
 * UKTZED picker для номенклатури (2026-06-02).
 *
 * Власник 2026-06-02: «у нас же є УКТЗЕД на сайті? можемо в Створенні
 * номенклатури в поле УКТЗЕД зробити підбор? як у Посади можна вибрати КП».
 *
 * Поведінка: read-only input який відкриває dropdown з search-полем.
 * Search йде через `GET /api/v1/refs/uktzed-codes?q=...` (FTS + trigram).
 *
 * Дзеркальний patern до KpCodePicker (PositionsPage.tsx).
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { uktzedCodes } from '@/api/catalogs';


interface Props {
  /** Поточний code (10 цифр) або null. */
  value: string | null;
  /** Колбек при виборі або очищенні. */
  onChange: (code: string | null, name: string | null) => void;
  /** Деактивувати picker (read-only режим). */
  disabled?: boolean;
}

export default function UktzedPicker({ value, onChange, disabled }: Props) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  // Якщо q має 10 цифр — шукаємо точний code. Інакше — full-text.
  const isCodeSearch = /^\d+$/.test(q);
  const searchParams = isCodeSearch
    ? { code_prefix: q, limit: 30 }
    : { q: q || undefined, limit: 30 };

  const searchQ = useQuery({
    queryKey: ['uktzed-search', q, isCodeSearch],
    queryFn: () => uktzedCodes.search(searchParams),
    enabled: open && q.length >= 2,
  });

  // Lookup поточного коду для display (показуємо назву поруч)
  const currentQ = useQuery({
    queryKey: ['uktzed-current', value],
    queryFn: () => uktzedCodes.search({ code_prefix: value!, limit: 1 }),
    enabled: !!value && value.length === 10,
  });
  const currentName = currentQ.data?.items?.[0]?.name_uk ?? null;

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const displayText = value
    ? `${value}${currentName ? ` — ${currentName}` : ''}`
    : 'УКТЗЕД (натисніть для пошуку)';

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        readOnly
        disabled={disabled}
        value={displayText}
        onClick={() => {
          if (disabled) return;
          setOpen(true);
          setQ('');
        }}
        placeholder="Пошук за кодом/назвою…"
        style={{ width: '100%', cursor: disabled ? 'not-allowed' : 'pointer' }}
        title={currentName ?? undefined}
      />
      {value && !disabled && (
        <button
          onClick={(e) => { e.stopPropagation(); onChange(null, null); }}
          style={{
            position: 'absolute',
            right: 4,
            top: 4,
            padding: '0.1rem 0.4rem',
            fontSize: '0.7rem',
          }}
          title="Очистити"
        >
          ×
        </button>
      )}
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
          background: 'white', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', marginTop: 4,
          maxHeight: 400, overflowY: 'auto', boxShadow: 'var(--shadow-lg)',
        }}>
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Введіть мін. 2 символи: код або частину назви…"
            style={{
              width: '100%',
              padding: '0.5rem',
              border: 'none',
              borderBottom: '1px solid var(--border)',
              borderRadius: 0,
            }}
          />
          {q.length < 2 ? (
            <div style={{ padding: '0.6rem', color: 'var(--muted)', fontSize: '0.85rem' }}>
              Введіть запит (≥ 2 символи). Шукати можна за:
              <ul style={{ margin: '0.4rem 0 0 1rem', padding: 0 }}>
                <li>Числовий код: <code>8517</code> (всі телефони)</li>
                <li>Назва: <code>яблука</code>, <code>пшениця</code>, <code>цемент</code></li>
              </ul>
            </div>
          ) : (searchQ.data?.items ?? []).length === 0 ? (
            <div style={{ padding: '0.6rem', color: 'var(--muted)', fontSize: '0.85rem' }}>
              {searchQ.isLoading ? 'Шукаю…' : 'Нічого не знайдено.'}
            </div>
          ) : (
            (searchQ.data!.items).map((e) => (
              <div
                key={e.id}
                onClick={() => {
                  onChange(e.code, e.name_uk);
                  setOpen(false);
                }}
                style={{
                  padding: '0.5rem 0.6rem',
                  cursor: 'pointer',
                  borderTop: '1px solid var(--border-soft)',
                  fontSize: '0.85rem',
                }}
                onMouseEnter={(ev) => (ev.currentTarget.style.background = 'var(--bg-soft)')}
                onMouseLeave={(ev) => (ev.currentTarget.style.background = 'white')}
              >
                <div>
                  <span style={{
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    marginRight: '0.5rem',
                  }}>
                    {e.code}
                  </span>
                  {e.name_uk}
                </div>
                {e.duty_rate_percent !== null && (
                  <div style={{
                    color: 'var(--muted)',
                    fontSize: '0.75rem',
                    marginTop: 2,
                  }}>
                    Ставка мита: {e.duty_rate_percent}%
                  </div>
                )}
              </div>
            ))
          )}
          {searchQ.data && searchQ.data.total > 30 && (
            <div style={{
              padding: '0.4rem 0.6rem',
              fontSize: '0.75rem',
              color: 'var(--muted)',
              borderTop: '1px solid var(--border-soft)',
            }}>
              Знайдено {searchQ.data.total} результатів. Уточніть запит щоб
              звузити список.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
